import React from "react";
import { Column } from "react-table";
import RequestTable from "./RequestTable";
import CustomAnswersTable from "./CustomAnswersTable";

export interface TableResults {
  sections: Section<any>[];
}

export interface Section<T extends Object> {
  title: string;
  columns: Array<Column<T>>;
  rows: Array<T & { row_override?: string | null }>;
  custom_answers?: Record<string, string> | null;
  // For procedure codes: array of { procedure_code, custom_answers }
  procedure_code_custom_answers?: Array<{
    procedure_code: string;
    custom_answers: Record<string, string>;
  }> | null;
}

// Wrapper component to catch errors per section
class SectionErrorBoundary extends React.Component<
  { children: React.ReactNode; sectionTitle: string },
  { hasError: boolean; error?: Error }
> {
  constructor(props: { children: React.ReactNode; sectionTitle: string }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error(`Error rendering section "${this.props.sectionTitle}":`, error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p className="text-yellow-800 font-medium">Unable to display: {this.props.sectionTitle}</p>
          <p className="text-yellow-600 text-sm">This section could not be rendered due to a data formatting issue.</p>
        </div>
      );
    }
    return this.props.children;
  }
}

// Check if a section has any actual data to display
function sectionHasData(section: Section<any>): boolean {
  // Check for custom answers first - if present, section should render
  if (section.custom_answers && Object.keys(section.custom_answers).length > 0) {
    return true;
  }
  if (section.procedure_code_custom_answers && section.procedure_code_custom_answers.length > 0) {
    return true;
  }

  // No columns or rows means no regular data
  if (!section.columns || section.columns.length === 0) {
    return false;
  }
  if (!section.rows || section.rows.length === 0) {
    return false;
  }

  // Check if any row has at least one non-null, non-empty value
  for (const row of section.rows) {
    for (const column of section.columns) {
      const accessor = column.accessor;
      if (accessor) {
        const key = typeof accessor === 'function' ? null : accessor.toString();
        if (key && key !== 'row_override') {
          const value = row[key];
          // If we find any value that's not null or empty string
          if (value != null && value !== "") {
            return true;
          }
        }
      }
    }
  }

  return false;
}

// Display Procedure code table when data present only
function isProcedureCodeTableUseless(section: Section<any>): boolean {
  if (!section.procedure_code_custom_answers?.length) return false;
  if (!section.rows?.length || !section.columns?.length) return true;

  const keysWithValues = new Set<string>();
  for (const row of section.rows) {
    for (const col of section.columns) {
      const key = typeof col.accessor === "string" ? col.accessor : null;
      if (key && key !== "row_override") {
        const val = row[key];
        if (val != null && val !== "") keysWithValues.add(key);
      }
    }
  }

  const meaningfulKeys = Array.from(keysWithValues).filter(
    (k) => k !== "procedure_code" && k !== "procedure_name"
  );
  if (meaningfulKeys.length > 0) return false;

  const procedureNames = section.rows
    .map((r: any) => r.procedure_name)
    .filter((v: unknown) => v != null && v !== "");
  const allSame =
    procedureNames.length <= 1 ||
    procedureNames.every((v: unknown) => v === procedureNames[0]);

  return allSame;
}

export function displayTableResults(
  results: TableResults,
  questionTextMap: Record<string, string> = {}
) {
  if (!results || !results.sections || !Array.isArray(results.sections)) {
    return null;
  }

  return (
    <div>
      {results.sections.map((section, index) => {
        if (!section) {
          return null;
        }

        // Skip rendering sections that have no actual data (including custom answers)
        if (!sectionHasData(section)) {
          return null;
        }

        const hasRegularData = section.columns && section.columns.length > 0 &&
                               section.rows && section.rows.length > 0;
        const hasCustomAnswers = section.custom_answers &&
                                 Object.keys(section.custom_answers).length > 0;
        const hasProcedureCodeCustomAnswers = section.procedure_code_custom_answers &&
                                               section.procedure_code_custom_answers.length > 0;
        const skipRequestTable = hasProcedureCodeCustomAnswers && isProcedureCodeTableUseless(section);

        return (
          <SectionErrorBoundary key={index} sectionTitle={section.title}>
            {/* Only render RequestTable if there's regular data and it's not useless */}
            {hasRegularData && !skipRequestTable && (
              <RequestTable
                title={section.title}
                data={section.rows}
                columns={section.columns}
              />
            )}
            {/* Render custom answers with title if no regular data table */}
            {hasCustomAnswers && (
              <CustomAnswersTable
                title={hasRegularData ? `${section.title} - Custom Questions` : section.title}
                customAnswers={section.custom_answers!}
                questionTextMap={questionTextMap}
              />
            )}
            {hasProcedureCodeCustomAnswers && (
              <CustomAnswersTable
                title={hasRegularData && !skipRequestTable ? `${section.title} - Custom Questions` : section.title}
                customAnswers={{}}
                questionTextMap={questionTextMap}
                procedureCodeCustomAnswers={section.procedure_code_custom_answers!}
              />
            )}
          </SectionErrorBoundary>
        );
      })}
    </div>
  );
}
